import { render } from "preact";

import App from "./App";
import {
  VideoAdSession,
} from "./application/VideoAdSession";
import renderAllPixels from "./pixels";
import { VideoAdProvider } from "./presentation/VideoAdContext";
import { AdsService } from "./services/ads";
import "./services/init-metrics";
import "./styles.css";
import type { AdVideoConfig, ResolvedVideoConfig } from "./types/config";
import { resolveVideoConfig } from "./types/config";
import type { VideoAdOutcome } from "./types/outcome";
import type { AdVideoHandle } from "./video-ads";

const SHIM_EXCLUDE_DOMAINS = [
  "arcade.hsn.com",
  "www.arkadium.com",
  "www-dev.arkadium.com",
  "www-dev2.arkadium.com",
  "www-dev3.arkadium.com",
  "www-dev4.arkadium.com",
];

function findContainer(config: AdVideoConfig): HTMLElement | null {
  if (config.element instanceof HTMLElement) return config.element;
  return document.getElementById(config.element || "ark_pre-roll");
}

const activeHandles = new WeakMap<HTMLElement, AdVideoHandle>();

class RenderHandle implements AdVideoHandle {
  private session?: VideoAdSession;
  private container?: HTMLElement;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private resolveFinished!: (outcome: VideoAdOutcome) => void;
  private settled = false;

  readonly finished = new Promise<VideoAdOutcome>((resolve) => {
    this.resolveFinished = resolve;
  });

  attach(session: VideoAdSession, container: HTMLElement): void {
    if (this.settled) {
      session.destroy();
      return;
    }
    this.session = session;
    this.container = container;
    void session.finished.then((outcome) => {
      if (activeHandles.get(container) === this) activeHandles.delete(container);
      this.settle(outcome);
    });
  }

  schedule(callback: () => void, milliseconds: number): void {
    this.retryTimer = setTimeout(callback, milliseconds);
  }

  fail(error: Error): void {
    this.settle({ reason: "failed", error });
  }

  destroy = (): void => {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.session?.destroy();
    if (this.container) render(null, this.container);
    this.settle({ reason: "destroyed" });
  };

  pause = (): void => this.session?.pause();
  resume = (): void => this.session?.resume();
  setVolume = (volume: number): void => this.session?.setVolume(volume);

  private settle(outcome: VideoAdOutcome): void {
    if (this.settled) return;
    this.settled = true;
    this.resolveFinished(outcome);
  }
}

class AdVideo {
  render(config: AdVideoConfig): AdVideoHandle {
    const handle = new RenderHandle();
    this.mount(config, handle, 0);
    return handle;
  }

  static preload(): void {
    console.warn("AdVideo#preload shouldn't be used anymore");
  }

  private mount(config: AdVideoConfig, handle: RenderHandle, retryCount: number): void {
    const container = findContainer(config);
    if (!container) {
      if (retryCount < 10) {
        handle.schedule(() => this.mount(config, handle, retryCount + 1), 500);
      } else {
        const error = new Error(
          `Can't find video ad container "${String(config.element)}"`,
        );
        console.error(error.message);
        handle.fail(error);
      }
      return;
    }

    renderAllPixels(container);
    activeHandles.get(container)?.destroy();
    container.classList.add("ark-preroll-container");
    this.applyArenaShim(container, config);

    const resolvedConfig: ResolvedVideoConfig = resolveVideoConfig(
      config,
      container,
    );
    const session = new VideoAdSession({
      config: resolvedConfig,
      service: new AdsService(),
    });
    handle.attach(session, container);
    activeHandles.set(container, handle);
    render(
      <VideoAdProvider session={session}>
        <App />
      </VideoAdProvider>,
      container,
    );
  }

  protected applyArenaShim(container: HTMLElement, config: AdVideoConfig): void {
    if (
      !["arena", "xml"].includes(config.product || "") ||
      SHIM_EXCLUDE_DOMAINS.includes(location.hostname)
    ) {
      return;
    }
    const parent = container.parentElement;
    if (!parent) return;
    const wrapper = document.createElement("div");
    parent.appendChild(wrapper);
    wrapper.appendChild(container);
  }
}

window.__ark_ads__ = window.__ark_ads__ || ({} as typeof window.__ark_ads__);
window.__ark_ads__.AdVideo = window.__ark_ads__.AdVideo || AdVideo;

export { AdVideo };
